import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../../components/PageHero";
import ShellCard from "../../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { renderControl } from "../renderControl";
import useFormularioParametros from "./useFormularioParametros";
import { montarAssuntos, contarAlteradosPorAssunto } from "./montarAssuntos";
import { paramParaControle, estaLigado } from "./paramParaControle";
import "../Parametros.css";
import "./ParametrosPorAssunto.css";

// Tela de parâmetros por assunto (H1, PO1-3259): PageHero + menu de assuntos à
// esquerda (vira select em tela estreita) + blocos em ShellCard com os campos do
// renderControl + barra inferior "Descartar / Salvar alterações" (só com alteração).
// Serve às três telas (Embarcador, Transportadora, Remetente); muda o mapa, o
// escopo e o contexto. `extrasPorAssunto` = { [key do assunto]: Componente } para
// blocos extras (ex.: a prévia do prazo de entrega, H6).
const toggleNomesStyle = (ativo) => ({
  background: ativo ? AUTOPILOT_COLORS.insetSurfaceActive : "transparent",
  border: `1px solid ${ativo ? "var(--theme-accent-border)" : AUTOPILOT_COLORS.borderFaint}`,
  color: ativo ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.text,
  fontWeight: 600,
  boxShadow: "none",
});

function CampoParametro({ campo, valor, dirty, mostrarNomes, salvando, confirmacao, onChange, onConfirmar, onCancelar }) {
  const pedindoConfirmacao = confirmacao && confirmacao.chave === String(campo.idParametro);
  return (
    <div className={`ppa-field${dirty ? " ppa-field--dirty" : ""}`}>
      <div className="ppa-field__info">
        <div className="ppa-field__label">
          <span>{campo.label}</span>
          {mostrarNomes ? <code className="ppa-field__nome">{campo.nome}</code> : null}
        </div>
        {campo.ajuda ? <div className="ppa-field__ajuda">{campo.ajuda}</div> : null}
      </div>
      <div className="ppa-field__control">
        {renderControl({
          param: paramParaControle(campo),
          valor,
          onChange,
          saving: salvando || !!pedindoConfirmacao,
          fullWidth: true,
        })}
      </div>
      {pedindoConfirmacao ? (
        <div className="ppa-confirm" role="alertdialog" aria-live="polite">
          <span>
            Desligar <strong>{campo.label}</strong>? Isso vale para todas as operações deste contexto a partir do próximo salvamento.
          </span>
          <Button color="warning" size="sm" onClick={onConfirmar}>
            Confirmar
          </Button>
          <Button color="secondary" size="sm" outline onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default function ParametrosPorAssunto({
  escopo,
  contextoId,
  mapa,
  titulo,
  subtitulo,
  eyebrow = "Configurações",
  cabecalhoExtra,
  extrasPorAssunto,
  mensagemSemContexto = "Selecione um registro para ver os parâmetros.",
}) {
  const {
    carregando,
    erro,
    formulario,
    alterados,
    totalAlterados,
    valoresAtuais,
    setValor,
    descartar,
    salvar,
    salvando,
    recarregar,
  } = useFormularioParametros(escopo, contextoId);

  const assuntos = useMemo(() => montarAssuntos(mapa, formulario), [mapa, formulario]);
  const contadores = useMemo(() => contarAlteradosPorAssunto(assuntos, alterados), [assuntos, alterados]);

  const [assuntoKey, setAssuntoKey] = useState(null);
  const [mostrarNomes, setMostrarNomes] = useState(false);
  const [confirmacao, setConfirmacao] = useState(null);

  const assuntoAtivo = assuntos.find((a) => a.key === assuntoKey) || assuntos[0] || null;

  // Trocou o contexto (outra transportadora/remetente): zera confirmação pendente.
  useEffect(() => {
    setConfirmacao(null);
  }, [contextoId, escopo]);

  const onChangeCampo = (campo, valor) => {
    const chave = String(campo.idParametro);
    if (campo.confirmarAoDesligar && estaLigado(valoresAtuais[chave]) && !estaLigado(valor)) {
      setConfirmacao({ chave, valor });
      return;
    }
    setValor(chave, valor);
  };

  const confirmarDesligar = () => {
    if (confirmacao) setValor(confirmacao.chave, confirmacao.valor);
    setConfirmacao(null);
  };

  const Extra = extrasPorAssunto && assuntoAtivo ? extrasPorAssunto[assuntoAtivo.key] : null;

  let corpo;
  if (!contextoId) {
    corpo = (
      <ShellCard style={{ marginTop: "1.25rem" }}>
        <div className="ppa-state">{mensagemSemContexto}</div>
      </ShellCard>
    );
  } else if (carregando) {
    corpo = (
      <ShellCard style={{ marginTop: "1.25rem" }}>
        <div className="ppa-state">
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
          <span>Carregando parâmetros...</span>
        </div>
      </ShellCard>
    );
  } else if (erro) {
    corpo = (
      <ShellCard style={{ marginTop: "1.25rem" }}>
        <div className="ppa-state">
          <span className="ppa-state--erro">{erro}</span>
          <Button size="sm" color="info" outline onClick={recarregar}>
            Tentar novamente
          </Button>
        </div>
      </ShellCard>
    );
  } else if (!assuntoAtivo) {
    corpo = (
      <ShellCard style={{ marginTop: "1.25rem" }}>
        <div className="ppa-state">Nenhum parâmetro disponível para este contexto.</div>
      </ShellCard>
    );
  } else {
    corpo = (
      <>
        <Input
          type="select"
          className="ppa-menu-select"
          value={assuntoAtivo.key}
          onChange={(e) => setAssuntoKey(e.target.value)}
          aria-label="Assunto"
        >
          {assuntos.map((a) => (
            <option key={a.key} value={a.key}>
              {a.titulo}
              {contadores[a.key] ? ` (${contadores[a.key]} alterado${contadores[a.key] === 1 ? "" : "s"})` : ""}
            </option>
          ))}
        </Input>

        <div className="ppa-layout">
          <nav className="ppa-menu" aria-label="Assuntos">
            {assuntos.map((a) => (
              <button
                key={a.key}
                type="button"
                className={`ppa-menu__item${a.key === assuntoAtivo.key ? " is-active" : ""}`}
                onClick={() => setAssuntoKey(a.key)}
                aria-current={a.key === assuntoAtivo.key ? "page" : undefined}
              >
                <span>{a.titulo}</span>
                {contadores[a.key] ? (
                  <span className="ppa-menu__badge" title={`${contadores[a.key]} alteração(ões) não salva(s)`}>
                    {contadores[a.key]}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>

          <div className="ppa-content">
            {assuntoAtivo.blocos.map((bloco, idx) => (
              <ShellCard
                key={`${assuntoAtivo.key}-${idx}`}
                eyebrow={idx === 0 ? assuntoAtivo.titulo : undefined}
                title={bloco.titulo}
                description={idx === 0 ? bloco.descricao || assuntoAtivo.descricao : bloco.descricao}
                action={
                  <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" }}>
                    {bloco.campos.length} parâmetro{bloco.campos.length === 1 ? "" : "s"}
                  </span>
                }
                bodyStyle={{ padding: "0.25rem 0.65rem 0.4rem" }}
              >
                {bloco.campos.map((campo) => (
                  <CampoParametro
                    key={campo.idParametro}
                    campo={campo}
                    valor={valoresAtuais[String(campo.idParametro)]}
                    dirty={Object.prototype.hasOwnProperty.call(alterados, String(campo.idParametro))}
                    mostrarNomes={mostrarNomes}
                    salvando={salvando}
                    confirmacao={confirmacao}
                    onChange={(v) => onChangeCampo(campo, v)}
                    onConfirmar={confirmarDesligar}
                    onCancelar={() => setConfirmacao(null)}
                  />
                ))}
              </ShellCard>
            ))}

            {Extra ? <Extra formulario={formulario} valoresAtuais={valoresAtuais} alterados={alterados} /> : null}
          </div>
        </div>

        {totalAlterados > 0 ? (
          <div className="ppa-savebar" role="region" aria-label="Alterações pendentes">
            <span className="ppa-savebar__texto">
              {totalAlterados} alteração{totalAlterados === 1 ? "" : "ões"} não salva{totalAlterados === 1 ? "" : "s"}
            </span>
            <Button color="secondary" size="sm" outline onClick={descartar} disabled={salvando}>
              Descartar
            </Button>
            <Button color="success" size="sm" onClick={salvar} disabled={salvando}>
              {salvando ? <Spinner size="sm" /> : "Salvar alterações"}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="content parametros-por-assunto">
      <PageHero
        eyebrow={eyebrow}
        title={titulo}
        description={subtitulo}
        actions={
          <>
            <Button
              className="btn-sm"
              style={toggleNomesStyle(mostrarNomes)}
              onClick={() => setMostrarNomes((v) => !v)}
              aria-pressed={mostrarNomes}
              title="Mostra o nome técnico de cada parâmetro ao lado do rótulo"
            >
              {mostrarNomes ? "Ocultar nomes técnicos" : "Mostrar nomes técnicos"}
            </Button>
            <PageHeroRefreshButton onClick={recarregar} loading={carregando} disabled={!contextoId} />
          </>
        }
      >
        {cabecalhoExtra ? <div style={{ marginTop: "1rem" }}>{cabecalhoExtra}</div> : null}
      </PageHero>

      {corpo}
    </div>
  );
}
